import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { InputFieldComponent } from '../../../../../../components/ui/inputField/inputField.component';
import { MessageAttributeChipsComponent } from '../../../../../../components/ui/message-attribute-chips/message-attribute-chips.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { removeMessageAttribute } from '../../../../../../../utils/utils';
import { Subscription } from 'rxjs';
import { TimeZoneService } from '../../../../../../services/timezone/time-zone.service';
import { IMessageTemplate, ISenderID } from '../../../../../../services/broadcast/types/broadcastTypes';
import { BroadcastMessageService } from '../../../../../../services/broadcast/broadcast.service';
import { SearchInputComponent } from '../../../../../../components/inputs/search-input/search-input.component';
import { SmsTextComponent } from '../../../../../../components/messages/sms-text/sms-text.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatCount, smsParts } from '../../../../../../../utils/sms';
import { SmsMeterComponent } from '../sms-meter/sms-meter.component';
import { BkSelectComponent } from '../../../../../../components/inputs/bk-select/bk-select.component';
import {
  localZone,
  ScheduleSummary,
  todayInZone,
  zoneGroups,
  ZoneGroups,
  zoneLabel,
} from '../../compose-schedule';

const TEMPLATE_PAGE_SIZE = 6;

/**
 * The editor: sender and subject, then whatever recipients panel the page
 * projects in, then the message with a live cost ruler and a template
 * browser, then when it goes out.
 */
@Component({
  selector: 'create-message-contents',
  standalone: true,
  imports: [
    InputFieldComponent,
    MessageAttributeChipsComponent,
    ReactiveFormsModule,
    SearchInputComponent,
    SmsTextComponent,
    MatTooltipModule,
    SmsMeterComponent,
    BkSelectComponent,
  ],
  templateUrl: './message-contents.component.html',
  styleUrl: './message-contents.component.css',
})
export class MessageContentsComponent implements OnInit, OnDestroy {
  constructor() {}
  timeZoneService = inject(TimeZoneService);
  broadcastService = inject(BroadcastMessageService);
  private subscriptions = new Subscription();

  senderIDs: ISenderID[] = [];
  senderLoading = true;

  @Input() submitted: boolean = false;
  @Input({ required: true }) messageContentForm!: FormGroup<{
    senderId: FormControl<string | null>;
    messageSubject: FormControl<string | null>;
    template: FormControl<null | string>;
    messageBody: FormControl<string | null>;
    messageType: FormControl<string | null>;
    timeZone: FormControl<string | null>;
    date: FormControl<string | null>;
    time: FormControl<string | null>;
  }>;
  /** The schedule in words, worked out by the page. */
  @Input() schedule: ScheduleSummary | null = null;
  @Input() scheduleInPast: boolean = false;

  formatCount = formatCount;
  zoneLabel = zoneLabel;

  get getTimeZones() {
    return this.timeZoneService.getTimeZones() || [];
  }

  private zoneCache: ZoneGroups | null = null;

  /** Suggested zones first (the browser's own on top), then every other zone. Built on first use. */
  get zones(): ZoneGroups {
    if (!this.zoneCache) {
      this.zoneCache = zoneGroups(this.getTimeZones.map((zone) => zone.timezone));
    }
    return this.zoneCache;
  }

  private minDateKey = '';
  private minDateCache = '';

  /** Earliest date the picker offers: today in the chosen zone. */
  get minDate(): string {
    const zone = this.messageContentForm.value.timeZone || '';
    const key = `${zone}|${Math.floor(Date.now() / 60000)}`;
    if (key !== this.minDateKey) {
      this.minDateKey = key;
      this.minDateCache = todayInZone(zone);
    }
    return this.minDateCache;
  }

  get isScheduled(): boolean {
    return this.messageContentForm.value.messageType === 'Scheduled';
  }

  // remove a contact-attribute placeholder from the message body (chip dismiss)
  removeAttribute(attribute: string) {
    const body = this.messageContentForm.controls.messageBody.value || '';
    this.messageContentForm.controls.messageBody.setValue(
      removeMessageAttribute(body, attribute)
    );
  }

  // ── Templates ──
  showTemplates = false;
  templates: IMessageTemplate[] = [];
  templatesLoading = false;
  templatesError = '';
  templateTerm = '';
  templatePage = 0;
  templateTotalPages = 1;
  templateTotal = 0;
  /** Name of the template the body started from, when known. */
  templateName = '';
  /** A template waiting for the operator to confirm it may replace their text. */
  pendingTemplate: IMessageTemplate | null = null;
  private templateRequest?: Subscription;

  toggleTemplates() {
    this.showTemplates = !this.showTemplates;
    this.pendingTemplate = null;
    if (!this.showTemplates) return;
    // The search box opens empty, so drop the last term rather than filter by it unseen.
    if (this.templateTerm || this.templatePage) {
      this.templateTerm = '';
      this.templatePage = 0;
      this.loadTemplates();
    } else if (!this.templates.length && !this.templatesLoading) {
      this.loadTemplates();
    }
  }

  onTemplateSearch(term: string) {
    this.templateTerm = term ?? '';
    this.templatePage = 0;
    this.loadTemplates();
  }

  goToTemplatePage(page: number) {
    if (page < 0 || page >= this.templateTotalPages || page === this.templatePage) return;
    this.templatePage = page;
    this.loadTemplates();
  }

  loadTemplates() {
    this.templateRequest?.unsubscribe();
    this.templatesLoading = true;
    this.templatesError = '';
    this.templateRequest = this.broadcastService
      .searchTemplates({
        page: this.templatePage,
        size: TEMPLATE_PAGE_SIZE,
        template_name: this.templateTerm,
        sortBy: 'nameAsc',
      })
      .subscribe({
        next: (page) => {
          this.templates = page.content;
          this.templateTotal = page.totalElements;
          this.templateTotalPages = page.totalPages;
          this.templatesLoading = false;
        },
        error: () => {
          this.templates = [];
          this.templatesLoading = false;
          this.templatesError = 'Templates did not load. Check your connection and try again.';
        },
      });
  }

  templateParts(content: string | null | undefined): string {
    const { parts, estimated } = smsParts(content || '');
    if (!parts) return 'Empty';
    const label = parts === 1 ? '1 part' : `${parts} parts`;
    return estimated ? `About ${label}` : label;
  }

  /** Use a template; ask first if it would replace different text. */
  chooseTemplate(template: IMessageTemplate) {
    const body = (this.messageContentForm.controls.messageBody.value || '').trim();
    const incoming = (template.content || '').trim();
    if (body && body !== incoming) {
      this.pendingTemplate = template;
      return;
    }
    this.useTemplate(template);
  }

  useTemplate(template: IMessageTemplate) {
    this.messageContentForm.patchValue({
      template: String(template.id ?? template.uuid ?? ''),
      messageBody: template.content || '',
    });
    this.templateName = template.name || '';
    this.pendingTemplate = null;
    this.showTemplates = false;
  }

  keepCurrentText() {
    this.pendingTemplate = null;
  }

  clearTemplate() {
    this.messageContentForm.controls.template.setValue('');
    this.templateName = '';
  }

  ngOnInit() {
    this.subscriptions.add(
      this.broadcastService.fetchSenderIDs().subscribe({
        complete: () => (this.senderLoading = false),
        error: () => (this.senderLoading = false),
      })
    );

    this.subscriptions.add(
      this.broadcastService.getSenderIDs$.subscribe({
        next: (value) => {
          this.senderIDs = value;
        },
      })
    );

    // Scheduling starts in the operator's own zone; they can change it.
    this.subscriptions.add(
      this.messageContentForm.controls.messageType.valueChanges.subscribe((type) => {
        if (type === 'Scheduled' && !this.messageContentForm.controls.timeZone.value) {
          this.messageContentForm.controls.timeZone.setValue(localZone());
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.templateRequest?.unsubscribe();
  }
}
